/**
 * Next up sections (Brett, 2026-10-16): "Next up projects are not
 * approved by default" — the band groups its rows into three sections
 * instead of carrying a decision pill on every requested row (the
 * pills "ran into the cost avoidance and EBITDA information").
 *
 *   1. Approved — anything past Requested (approved, in progress, …)
 *   2. Requested · brief received — kickstart brief submitted/approved
 *   3. Requested · no brief yet
 *
 * Order WITHIN a section is the band's rank order, so re-ranking
 * still works; the sections only decide which block a row sits in.
 */

export type NextUpSectionKey = "approved" | "briefed" | "unbriefed";

export type NextUpRow = { status?: string | null; brief_status?: string | null };

export type NextUpSection<T extends NextUpRow> = {
  key: NextUpSectionKey;
  label: string;
  hint: string;
  rows: T[];
};

export function isRequested(row: NextUpRow): boolean {
  return (row.status ?? "").toLowerCase() === "requested";
}

export function isBriefReceived(row: NextUpRow): boolean {
  const s = (row.brief_status ?? "").toLowerCase();
  return s === "submitted" || s === "approved";
}

export function nextUpSectionKey(row: NextUpRow): NextUpSectionKey {
  if (!isRequested(row)) return "approved";
  return isBriefReceived(row) ? "briefed" : "unbriefed";
}

const META: Record<NextUpSectionKey, { label: string; hint: string }> = {
  approved: { label: "Approved", hint: "Approved to proceed as resources allow" },
  briefed: { label: "Requested · brief received", hint: "Kickstart brief is in hand — ready for a decision" },
  unbriefed: { label: "Requested · no brief yet", hint: "Waiting on the kickstart brief before a decision" },
};

/** Non-empty sections, in display order, each keeping the input order. */
export function nextUpSections<T extends NextUpRow>(rows: T[]): NextUpSection<T>[] {
  const buckets: Record<NextUpSectionKey, T[]> = { approved: [], briefed: [], unbriefed: [] };
  for (const r of rows) buckets[nextUpSectionKey(r)].push(r);
  return (["approved", "briefed", "unbriefed"] as const)
    .filter((k) => buckets[k].length > 0)
    .map((k) => ({ key: k, ...META[k], rows: buckets[k] }));
}
